import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { SeparatorIcon } from "components/icons/SeparatorIcon";
import { cn } from "helpers/classNames";
import { forwardRef, useEffect, useState } from "react";
import { TransportSedel } from "types/transportSedel.type";
import { Uppdrag } from "types/uppdrag.type";
import { getRelevantWasteWeight, Waste } from "types/waste.type";
import { getSeparatorDetailedLabel } from "types/separator/separator.helpers";
import { CreateWasteLogicRetVal } from "../useCreateWasteLogic";
import { useDeleteWasteLogic } from "../useDeleteWasteLogic";
import { useUpdateWasteLogic } from "../useUpdateWasteLogic";
import { OperatorDangerSheet } from "./OperatorDangerSheet";
import { OperatorWasteSheet, WasteWeightField } from "./OperatorWasteSheet";

export type WasteEditRequest = {
  wasteId: number;
  highlightField?: WasteWeightField;
};

type Props = {
  transportSedel: TransportSedel;
  uppdrag: Uppdrag;
  createWasteLogic: CreateWasteLogicRetVal;
  isHighlighted?: boolean;
  wasteEditRequest?: WasteEditRequest | null;
  onWasteEditRequestHandled?: () => void;
};

export const OperatorWasteSection = forwardRef<HTMLElement, Props>(
  (
    {
      transportSedel,
      uppdrag,
      createWasteLogic,
      isHighlighted,
      wasteEditRequest,
      onWasteEditRequestHandled,
    },
    ref,
  ) => {
    return (
      <section
        ref={ref}
        className={cn(
          "scroll-mt-24 rounded-xl transition-shadow duration-300",
          isHighlighted && "ring-2 ring-blue-400 ring-offset-4",
        )}
      >
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Avfall
          {transportSedel.wasteList.length > 0 && (
            <span className="ml-1.5 rounded-full bg-gray-50 px-2 py-0.5 font-semibold normal-case text-gray-600">
              {transportSedel.wasteList.length}
            </span>
          )}
        </h4>
        <div className="flex flex-col gap-y-2">
          {transportSedel.wasteList
            .slice()
            .sort((a, b) => (a.id < b.id ? -1 : 1))
            .map((waste) => (
              <OperatorWasteCard
                key={waste.id}
                waste={waste}
                transportSedel={transportSedel}
                uppdrag={uppdrag}
                editRequest={
                  wasteEditRequest?.wasteId === waste.id
                    ? wasteEditRequest
                    : null
                }
                onEditRequestHandled={onWasteEditRequestHandled}
              />
            ))}
          <button
            type="button"
            onClick={createWasteLogic.openModal}
            className="flex h-12 w-full items-center justify-center gap-x-1.5 rounded-xl border-2 border-dashed border-gray-200 text-sm font-semibold text-gray-600 transition-colors active:border-blue-300 active:bg-blue-50 active:text-blue-700"
          >
            <PlusIcon
              className="h-5 w-5"
              aria-hidden="true"
              strokeWidth={2.5}
            />
            Lägg till avfall
          </button>
        </div>
      </section>
    );
  },
);
OperatorWasteSection.displayName = "OperatorWasteSection";

type CardProps = {
  waste: Waste;
  transportSedel: TransportSedel;
  uppdrag: Uppdrag;
  editRequest?: WasteEditRequest | null;
  onEditRequestHandled?: () => void;
};

const OperatorWasteCard = ({
  waste,
  transportSedel,
  uppdrag,
  editRequest,
  onEditRequestHandled,
}: CardProps) => {
  const editWasteLogic = useUpdateWasteLogic(transportSedel, uppdrag, waste);
  const deleteWasteLogic = useDeleteWasteLogic();
  const weight = getRelevantWasteWeight(waste);

  // Öppnas utifrån (checklistan) via request-props, med markerat viktfält
  const [highlightField, setHighlightField] = useState<
    WasteWeightField | undefined
  >(undefined);
  const { openModal } = editWasteLogic;
  useEffect(() => {
    if (!editRequest) return;
    setHighlightField(editRequest.highlightField);
    openModal();
    onEditRequestHandled?.();
  }, [editRequest, openModal, onEditRequestHandled]);

  useEffect(() => {
    if (!editWasteLogic.isModalOpen) {
      setHighlightField(undefined);
    }
  }, [editWasteLogic.isModalOpen]);

  return (
    <div className="relative rounded-xl border border-gray-75 bg-white">
      <button
        type="button"
        onClick={editWasteLogic.openModal}
        className="w-full rounded-xl p-4 pr-12 text-left transition-colors active:bg-gray-12"
      >
        {!!waste.tomning?.separator && (
          <p className="mb-1.5 flex items-center gap-x-1 truncate text-xs text-gray-500">
            <SeparatorIcon className="shrink-0" />
            {getSeparatorDetailedLabel(waste.tomning.separator)}
          </p>
        )}
        <p className="text-base font-bold text-gray-900">
          {waste.wasteCode.codePretty}
        </p>
        <p className="text-sm text-gray-600">{waste.wasteCode.description}</p>
        <div className="mt-2.5">
          {weight ? (
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset",
                waste.weightFinal
                  ? "bg-green-50 text-green-700 ring-green-600/20"
                  : "bg-gray-25 text-gray-700 ring-gray-400/30",
              )}
            >
              {weight}
              {!!waste.weightFinal && <span className="ml-1">· Vägd</span>}
            </span>
          ) : (
            <span className="inline-flex items-center gap-x-1 rounded-full bg-yellow-50 px-2.5 py-1 text-xs font-semibold text-yellow-800 ring-1 ring-inset ring-yellow-600/20">
              <ExclamationTriangleIcon
                className="h-3.5 w-3.5 text-yellow-600"
                aria-hidden="true"
              />
              Vikt saknas
            </span>
          )}
        </div>
      </button>
      <button
        type="button"
        onClick={deleteWasteLogic.openModal}
        aria-label="Ta bort avfall"
        className="absolute right-2 top-2 rounded-full p-2 text-gray-400 transition-colors active:bg-red-50 active:text-red-500"
      >
        <TrashIcon className="h-5 w-5" aria-hidden="true" />
      </button>
      <OperatorWasteSheet
        logic={editWasteLogic}
        title={"Redigera avfall"}
        highlightField={highlightField}
      />
      <OperatorDangerSheet
        title="Ta bort avfall"
        isOpen={deleteWasteLogic.isModalOpen}
        onClose={deleteWasteLogic.onModalClose}
        actionHandler={() =>
          deleteWasteLogic.onConfirm({
            transportSedelId: transportSedel.id,
            wasteId: waste.id,
          })
        }
      />
    </div>
  );
};
